import {Box, Button, ButtonGroup, Field, IconButton, Input, Pagination, Show, Stack, Table, Textarea} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Company, dataApi, Document as Doc} from "../../api/data-api";
import {DateTime} from "luxon";
import {useToast} from "@chakra-ui/toast";
import {useRecoilState} from "recoil";
import {companyData, showTfaCheck, userData} from "../../components/hooks/recoilData";
import {LuChevronLeft, LuChevronRight} from "react-icons/lu";
import {DocumentFile} from "./documentFile";
import {errorNotify} from "../../components/hooks/CommonUtils";
import {DocumentExt} from "./index";

export const DocumentList = (props: {
    companies: Company[],
    documentDirectionType: "FROM" | "TO",
    companyId: number | null,
    userId: number | null,
    editable: boolean
}) => {
    const [items, setItems] = useState<Doc[]>([]);
    const [count, setCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 10;
    const toast = useToast();
    const [user] = useRecoilState(userData);
    const [, setTfa] = useRecoilState(showTfaCheck);
    // const [executor] = useRecoilState(executorData);
    const [company] = useRecoilState(companyData);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);

    const inputDefault: DocumentExt = useMemo(() => {
        return {
            id: null,
            type: "MANUAL",
            userId: user ? String(user.ID) : "",
            toUserId: user ? String(user.ID) : "",
            companyId: company ? String(company.ID) : (props.companies.length > 0 ? String(props.companies[0].ID) : ""),
            content: "",
            additionalInfo: "",
            sign: "",
            editable: false,
        }
    }, [user, company, props]);
    const [inputParams, setInputParams] = useState<DocumentExt>(inputDefault);

    useEffect(() => {
        dataApi.documentListCount(props.companyId, props.userId, props.documentDirectionType)
            .then(o => {
                if (o && o > 0)
                    setCount(o as number);
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast, props]);
    useEffect(() => {
        dataApi.documentList(props.companyId, props.userId, props.documentDirectionType, (currentPage - 1) * pageSize, pageSize)
            .then(o => {
                if (o)
                    setItems(o);
            })
            .catch((err) => errorNotify(err, toast));
    }, [count, currentPage, toast, props]);

    const ready = useCallback((d: Doc) => {
        if (d.READY || d.SIGNED || d.REJECTED)
            return;
        dataApi.documentReady(d.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Ready", status: "success"});
                    d.READY = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(d);
                        if (index > -1)
                            lst[index] = d;
                        return lst;
                    });
                } else {
                    toast({title: "Ready failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);
    const sign = useCallback((d: Doc) => {
        if (!d.READY || d.SIGNED || d.REJECTED)
            return;
        dataApi.documentSign(d.ID)
            .then((o) => {
                setTfa(false);
                setLoading(false);
                if (o) {
                    toast({title: "Sign", status: "success"});
                    d.SIGNED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(d);
                        if (index > -1)
                            lst[index] = o;
                        return lst;
                    });
                } else {
                    toast({title: "Sign failed", status: "error"});
                }
            })
            .catch((err) => {
                setTfa(false);
                setLoading(false);
                errorNotify(err, toast);
            });
        setTfa(true);
        setLoading(true);
    }, [setTfa, toast]);
    const reject = useCallback((d: Doc) => {
        if (!d.READY || d.SIGNED || d.REJECTED)
            return;
        dataApi.documentReject(d.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Reject", status: "success"});
                    d.REJECTED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(d);
                        if (index > -1)
                            lst[index] = d;
                        return lst;
                    });
                } else {
                    toast({title: "Reject failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);
    const documentDelete = useCallback((d: Doc) => {
        if (d.READY || d.SIGNED || d.REJECTED)
            return;
        dataApi.documentDelete(d.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== d.ID);
                    })
                } else {
                    toast({title: "Delete failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const prepareInput = useCallback((item: Doc | null) => {
        setInputParams({
            id: item ? item.ID : null,
            type: item ? item.TYPE1 : "MANUAL",
            userId: item ? (item.USER_ID ? String(item.USER_ID) : "") : (user ? String(user.ID) : ""),
            toUserId: item ? (item.TO_USER_ID ? String(item.TO_USER_ID) : "") : (user ? String(user.ID) : ""),
            companyId: item ? (item.COMPANY_ID ? String(item.COMPANY_ID) : "") : (company ? String(company.ID) : (props.companies.length > 0 ? String(props.companies[0].ID) : "")),
            content: item ? item.CONTENT : "",
            additionalInfo: item && item.ADDITIONAL_INFO ? item.ADDITIONAL_INFO : "",
            sign: item && item.CONTENT_SIGN ? item.CONTENT_SIGN : "",
            editable: item ? user?.ID === item.USER_ID && !item.READY && !item.SIGNED && !item.REJECTED : true,
        });
    }, [props, company, user]);

    const createOrUpdate = useCallback(() => {
        if (!inputParams.userId || !inputParams.toUserId)
            return;
        dataApi.documentCreateOrUpdate(inputParams.id, parseInt(inputParams.toUserId), inputParams.companyId ? parseInt(inputParams.companyId) : null, inputParams.content, false)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    let findO = prev.find(o => o.ID === value.ID);
                    const lst = [...prev];
                    if (findO) {
                        lst[prev.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                if (!inputParams.id) {
                    setInputParams(prev => {
                        return {...prev, id: value.ID};
                    });
                }
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast, inputParams, prepareInput]);

    return (
        <Stack width="full" gap="5">
            <Show when={props.editable}>
                <Box>
                    <Button onClick={e => {
                        if (showForm) {
                            setShowForm(false);
                        } else {
                            prepareInput(null);
                            setShowForm(true);
                        }
                    }}>
                        Создать новый
                    </Button>
                </Box>
            </Show>
            <Show when={showForm}>
                <Box borderWidth={1} borderColor="gray">
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>Пользователю <Field.RequiredIndicator/></Field.Label>
                        <Input type="text" placeholder="Пользователю"
                               value={String(inputParams.toUserId)}
                               onChange={(it) => setInputParams((prev) => {
                                   return {...prev, toUserId: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Компания</Field.Label>
                        <Input type="text" placeholder="Компания"
                               value={String(inputParams.companyId)}
                               onChange={(it) => setInputParams((prev) => {
                                   return {...prev, companyId: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>Содержание <Field.RequiredIndicator/></Field.Label>
                        <Textarea placeholder="Содержание"
                                  value={inputParams.content}
                                  onChange={(it) => setInputParams((prev) => {
                                      return {...prev, content: it.target.value}
                                  })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Дополнительная информация <Field.RequiredIndicator/></Field.Label>
                        <Textarea placeholder="Дополнительная информация"
                                  disabled={true}
                                  value={inputParams.additionalInfo}
                                  onChange={(it) => setInputParams((prev) => {
                                      return {...prev, additionalInfo: it.target.value}
                                  })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal">
                        <Field.Label>Подпись <Field.RequiredIndicator/></Field.Label>
                        <Textarea placeholder="Подпись"
                                  disabled={true}
                                  value={inputParams.sign}
                                  onChange={(it) => setInputParams((prev) => {
                                      return {...prev, sign: it.target.value}
                                  })}/>
                    </Field.Root>
                    <Show when={props.editable}>
                        <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                                onClick={e => createOrUpdate()}
                                disabled={!inputParams.editable}>
                            {inputParams.id ? "Сохранить" : "Создать"}
                        </Button>
                    </Show>

                    {inputParams.id ? (<DocumentFile documentId={inputParams.id} editable={props.editable}/>) : null}
                </Box>

            </Show>
            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Id</Table.ColumnHeader>
                        <Table.ColumnHeader>Тип</Table.ColumnHeader>
                        <Show when={props.userId === null}>
                            <Table.ColumnHeader>Пользователь</Table.ColumnHeader>
                            <Table.ColumnHeader>Компания</Table.ColumnHeader>
                        </Show>
                        <Table.ColumnHeader>Содержание</Table.ColumnHeader>
                        <Table.ColumnHeader>Дополнительная информация</Table.ColumnHeader>
                        <Table.ColumnHeader>Создан</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Готов</Table.ColumnHeader>
                        <Table.ColumnHeader>Подписан</Table.ColumnHeader>
                        <Table.ColumnHeader>Отменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.ID}</Table.Cell>
                            <Table.Cell>{item.TYPE1}</Table.Cell>
                            <Show when={props.userId === null}>
                                <Table.Cell>{props.documentDirectionType === "TO" ? (user?.ID === item.USER_ID ? "Я" : item.USER_ID) : (user?.ID === item.TO_USER_ID ? "Я" : item.TO_USER_ID)}</Table.Cell>
                                <Table.Cell>{item.COMPANY_ID ? props.companies.find(o => o.ID === item.COMPANY_ID)?.NAME : item.COMPANY_ID}</Table.Cell>
                            </Show>
                            <Table.Cell>{item.CONTENT && item.CONTENT.length > 50 ? item.CONTENT.substring(0, 50) : item.CONTENT}</Table.Cell>
                            <Table.Cell>{item.ADDITIONAL_INFO && item.ADDITIONAL_INFO.length > 50 ? item.ADDITIONAL_INFO.substring(0, 50) : item.ADDITIONAL_INFO}</Table.Cell>
                            <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.READY ? DateTime.fromMillis(item.READY).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.SIGNED ? DateTime.fromMillis(item.SIGNED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.REJECTED ? DateTime.fromMillis(item.REJECTED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => {
                                    prepareInput(item);
                                    setShowForm(true);
                                }}>
                                    Показать
                                </Button>
                                <Show when={props.editable}>
                                    <Show when={user?.ID === item.TO_USER_ID && item.READY && !item.SIGNED && !item.REJECTED}>
                                        <Button
                                            // display={item.READY && !item.SIGNED && !item.REJECTED ? "block" : "none"}
                                            loading={loading}
                                            onClick={e => sign(item)}>
                                            Подписать
                                        </Button>
                                        <Button
                                            // display={item.READY && !item.SIGNED && !item.REJECTED ? "block" : "none"}
                                            loading={loading}
                                            onClick={e => reject(item)}>
                                            Отменить
                                        </Button>
                                    </Show>
                                    <Show when={user?.ID === item.USER_ID && !item.READY && !item.SIGNED && !item.REJECTED}>
                                        <Button onClick={e => ready(item)}>
                                            Готов
                                        </Button>
                                        <Button onClick={e => documentDelete(item)}>Удалить</Button>
                                    </Show>
                                </Show>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

            <Pagination.Root count={count} pageSize={pageSize} page={currentPage}>
                <ButtonGroup variant="ghost" size="sm" wrap="wrap">
                    <Pagination.PrevTrigger asChild>
                        <IconButton>
                            <LuChevronLeft/>
                        </IconButton>
                    </Pagination.PrevTrigger>

                    <Pagination.Items
                        render={(page) => (
                            <IconButton variant={{base: "ghost", _selected: "outline"}} onClick={e => setCurrentPage(page.value)}>
                                {page.value}
                            </IconButton>
                        )}
                    />

                    <Pagination.NextTrigger asChild>
                        <IconButton>
                            <LuChevronRight/>
                        </IconButton>
                    </Pagination.NextTrigger>
                </ButtonGroup>
            </Pagination.Root>
        </Stack>
    );
};